import type { SchoolAnalyticsRow } from "@/client"
import { Box, Card, Heading, Text } from "@chakra-ui/react"
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

const TOP_SCHOOL_COUNT = 20
const CHART_COLORS = {
  started: "#4A5568",
  completed: "#3182CE",
  score: "#38A169",
  completion: "#805AD5",
}

const SCHOOL_AXIS_LABEL_LENGTH = 26

export function formatSchoolChartLabel(name: string): string {
  return name.length <= SCHOOL_AXIS_LABEL_LENGTH
    ? name
    : `${name.slice(0, SCHOOL_AXIS_LABEL_LENGTH - 1)}…`
}

export function getTopSchoolRows(
  rows: SchoolAnalyticsRow[],
): SchoolAnalyticsRow[] {
  return rows
    .map((school, sourceIndex) => ({ school, sourceIndex }))
    .sort((left, right) => {
      const startedDifference =
        right.school.started_attempts - left.school.started_attempts
      return startedDifference || left.sourceIndex - right.sourceIndex
    })
    .slice(0, TOP_SCHOOL_COUNT)
    .map(({ school }) => school)
}

interface BarSpec {
  dataKey: string
  fill: string
  name: string
}

interface SchoolChartCardProps {
  ariaLabel: string
  bars: BarSpec[]
  data: SchoolAnalyticsRow[]
  subtitle: string
  testId: string
  title: string
  xAxisProps?: { domain?: [number, number] }
}

const chartStyle = {
  color: "var(--chakra-colors-fg)",
  backgroundColor: "var(--chakra-colors-bg-panel)",
  borderColor: "var(--chakra-colors-border)",
}

function SchoolChartCard({
  ariaLabel,
  bars,
  data,
  subtitle,
  testId,
  title,
  xAxisProps,
}: SchoolChartCardProps) {
  return (
    <Card.Root
      role="img"
      aria-label={ariaLabel}
      data-testid={testId}
      data-school-keys={data.map((school) => school.school_key).join(",")}
    >
      <Card.Header>
        <Heading size="md">{title}</Heading>
        <Text color="fg.muted" fontSize="sm">
          {subtitle}
        </Text>
      </Card.Header>
      <Card.Body>
        <Box h={{ base: "520px", md: "640px" }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              layout="vertical"
              margin={{ top: 8, right: 24, left: 16, bottom: 8 }}
            >
              <CartesianGrid
                stroke="var(--chakra-colors-border)"
                strokeDasharray="3 3"
              />
              <XAxis
                type="number"
                allowDecimals={false}
                tick={{
                  fill: "var(--chakra-colors-fg-muted)",
                  fontSize: 11,
                }}
                {...xAxisProps}
              />
              <YAxis
                type="category"
                dataKey="school_name"
                tickFormatter={formatSchoolChartLabel}
                width={180}
                tick={{
                  fill: "var(--chakra-colors-fg-muted)",
                  fontSize: 11,
                }}
              />
              <Tooltip contentStyle={chartStyle} />
              {bars.map((bar) => (
                <Bar
                  key={bar.dataKey}
                  dataKey={bar.dataKey}
                  fill={bar.fill}
                  name={bar.name}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Card.Body>
    </Card.Root>
  )
}

interface SchoolAnalyticsChartsProps {
  rows: SchoolAnalyticsRow[]
}

export function SchoolAnalyticsCharts({ rows }: SchoolAnalyticsChartsProps) {
  const topSchools = getTopSchoolRows(rows)
  const scoreSchools = topSchools.filter(
    (school) =>
      school.metrics_hidden_reason !== "minimum_cohort_not_met" &&
      school.avg_score_percentage !== null &&
      school.avg_score_percentage !== undefined,
  )

  if (topSchools.length === 0) {
    return (
      <Text color="fg.muted" data-testid="school-analytics-charts-empty">
        No school analytics to chart.
      </Text>
    )
  }

  return (
    <Box data-testid="school-analytics-charts">
      <Heading size="sm" mb={3}>
        Ranked school views
      </Heading>
      <Box display="grid" gap={6}>
        <SchoolChartCard
          ariaLabel="Top 20 schools by started and completed attempts"
          bars={[
            {
              dataKey: "started_attempts",
              fill: CHART_COLORS.started,
              name: "Started",
            },
            {
              dataKey: "completed_attempts",
              fill: CHART_COLORS.completed,
              name: "Completed",
            },
          ]}
          data={topSchools}
          subtitle="All top-20 schools remain visible, including small cohorts."
          testId="school-volume-chart"
          title="Top schools by activity"
        />

        {scoreSchools.length > 0 ? (
          <SchoolChartCard
            ariaLabel="Top 20 schools by student-weighted average score"
            bars={[
              {
                dataKey: "avg_score_percentage",
                fill: CHART_COLORS.score,
                name: "Avg score %",
              },
            ]}
            data={scoreSchools}
            subtitle="Small cohorts stay excluded from score metrics."
            testId="school-score-chart"
            title="Top school scores"
            xAxisProps={{ domain: [0, 100] }}
          />
        ) : (
          <Text color="fg.muted" data-testid="school-score-chart-empty">
            No school scores meet the minimum cohort requirement.
          </Text>
        )}

        <SchoolChartCard
          ariaLabel="Top 20 schools by completion rate"
          bars={[
            {
              dataKey: "completion_rate",
              fill: CHART_COLORS.completion,
              name: "Completion rate %",
            },
          ]}
          data={topSchools}
          subtitle="Completion remains visible for every ranked school."
          testId="school-completion-chart"
          title="Top school completion rates"
          xAxisProps={{ domain: [0, 100] }}
        />
      </Box>
    </Box>
  )
}
